// Done — instant release (overhaul §6.2, panel 04). Releases the moment the source connects
// (no waiting on a real capture). Copy adapts per branch; the live indicator is decorative
// (a pulsing dot + status text), never a fabricated count.
//
// "Go to dashboard" PERSISTS first-run completion before navigating (onEnter awaits the
// gate's completion mutation) — not a local-only state flip — so a reload stays on the
// dashboard. A failed write keeps the user here with a retry, never silently releasing.

import { useState } from 'react';
import { siteLabel } from '../../lib/sites';
import { deriveBranchKind } from './wizard-step';

interface DoneCopy {
  title: string;
  sub: string;
  status: string;
}

function doneCopy(sourceId: string | null): DoneCopy {
  const label = sourceId ? siteLabel(sourceId) : 'Your source';
  switch (sourceId ? deriveBranchKind(sourceId) : 'chat') {
    case 'google':
      return {
        title: 'Google connected!',
        sub: 'Mirroring your recent Gmail & Docs now. Your dashboard fills in as it syncs.',
        status: 'Syncing your Gmail & Docs…',
      };
    case 'claudecode':
      return {
        title: 'Claude Code linked!',
        sub: 'Your next Claude Code session will sync automatically. Head to the dashboard to watch it land.',
        status: 'Hook installed — listening for sessions',
      };
    default:
      return {
        title: "You're all set!",
        sub: `${label} connected — importing now. Watch it arrive on your dashboard.`,
        status: 'Importing your conversations…',
      };
  }
}

export function DoneStep({ sourceId, onEnter }: { sourceId: string | null; onEnter: () => void | Promise<void> }) {
  const copy = doneCopy(sourceId);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const go = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await onEnter();
      // on success the gate unmounts this screen — no need to clear busy
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <>
      <div className="crumb">STEP 04 · YOU'RE SET</div>
      <div className="wh">{copy.title}</div>
      <p className="wp">{copy.sub}</p>
      <div style={{ marginTop: 22, display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--inkS)' }}>
        <span className="dot acc pulse" />
        {copy.status}
      </div>
      <div className="wiz-foot">
        <button className="btn btn-primary" disabled={busy} onClick={go}>
          {busy ? 'Saving…' : <>Go to dashboard <span className="arr">→</span></>}
        </button>
        {failed && (
          <span style={{ fontSize: 13, color: 'var(--rose)' }}>Couldn't save — please try again.</span>
        )}
        <span className="prog">Step 4 of 4</span>
      </div>
    </>
  );
}
